/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */

import type { CreateLocalTracksOptions, LocalTrack } from "twilio-video";

import {
  connect,
  createLocalTracks,
  createLocalVideoTrack,
  LocalAudioTrack,
  LocalVideoTrack,
  Room,
} from "twilio-video";

import type { LiveConsultConstructorParameters } from "@/App/components/liveConsult/types/LiveConsultConstructorParameters";
import type { LiveConsultService } from "@/App/components/liveConsult/types/LiveConsultService";
import type { PickUpLiveConsultParameters } from "@/App/components/liveConsult/types/PickUpLiveConsultParameters";

import { LiveConsultEvents } from "@/App/components/liveConsult/types/LiveConsultEvents";

export interface LiveConsult {
  audioIsDisabled: boolean;
  caseId: string;
  liveConsultService: LiveConsultService;
  localVideoTrack: LocalVideoTrack | undefined;
  onLocalVideoTrackAvailable?: (track: LocalVideoTrack | undefined) => void;
  room: Room;
  tracks: LocalTrack[] | LocalAudioTrack[] | LocalVideoTrack[];
  videoIsDisabled: boolean;
}

export class LiveConsult implements LiveConsult {
  private pickedUp = false;
  onLocalVideoTrackAvailable?: (track: LocalVideoTrack | undefined) => void;

  constructor({ caseId, liveConsultService, startWithVideoDisabled = false }: LiveConsultConstructorParameters) {
    this.audioIsDisabled = false;
    this.caseId = caseId;
    this.liveConsultService = liveConsultService;
    this.videoIsDisabled = startWithVideoDisabled;
  }

  hangUp(): void {
    this.room?.disconnect();

    this.localVideoTrack?.stop();
    this.localVideoTrack?.detach();

    (this.tracks ?? []).forEach((track: LocalTrack) => {
      if (track instanceof LocalAudioTrack || track instanceof LocalVideoTrack) {
        track?.stop();
      }
    });
  }

  async pickUp({ onParticipantConnected, onParticipantDisconnected }: PickUpLiveConsultParameters): Promise<void> {
    if (this.pickedUp) return;

    const { authToken, roomId } = await this.liveConsultService.getLiveConsultToken({
      caseId: this.caseId,
    });

    const trackOptions: CreateLocalTracksOptions | undefined = this.videoIsDisabled
      ? {
          audio: true,
          video: false,
        }
      : undefined;

    this.tracks = await createLocalTracks(trackOptions);

    this.room = await connect(authToken, { name: roomId, tracks: this.tracks });

    this.pickedUp = true;

    this.room.participants.forEach(onParticipantConnected);

    this.room.on(LiveConsultEvents.participantConnected, onParticipantConnected);

    this.room.on(LiveConsultEvents.participantDisconnected, onParticipantDisconnected);

    this.room.once(LiveConsultEvents.disconnected, () => this.room.participants.forEach(onParticipantDisconnected));

    if (!this.videoIsDisabled) {
      const firstLocalVideoPub = Array.from(this.room.localParticipant.videoTracks.values())[0];
      const publishedLocalVideoTrack = firstLocalVideoPub?.track;

      if (publishedLocalVideoTrack) {
        this.localVideoTrack = publishedLocalVideoTrack;
      } else {
        this.localVideoTrack = await createLocalVideoTrack();
        await this.room.localParticipant.publishTrack(this.localVideoTrack);
      }
    } else {
      this.localVideoTrack = undefined;
    }
  }

  toggleAudio(): void {
    if (!this.room) return;

    const method = this.audioIsDisabled ? "enable" : "disable";

    this.room.localParticipant.audioTracks.forEach((publication) => {
      const track = publication.track;
      track?.[method]?.();
    });
    this.audioIsDisabled = !this.audioIsDisabled;
  }

  async toggleVideo(): Promise<void> {
    if (!this.room) return;

    if (this.videoIsDisabled) {
      this.localVideoTrack = await createLocalVideoTrack();
      await this.room.localParticipant.publishTrack(this.localVideoTrack);
      this.onLocalVideoTrackAvailable?.(this.localVideoTrack);

      this.videoIsDisabled = false;
      return;
    }

    this.room.localParticipant.videoTracks.forEach((publication) => {
      const track = publication.track;
      if (track) {
        this.room?.localParticipant.unpublishTrack(track);
        track.detach?.();
        track.stop?.();
      }
    });

    this.localVideoTrack?.detach?.();
    this.localVideoTrack?.stop?.();
    this.localVideoTrack = undefined;

    this.onLocalVideoTrackAvailable?.(undefined);

    this.videoIsDisabled = true;
  }
}
export type { RemoteParticipant, RemoteTrackPublication, RemoteVideoTrack } from "twilio-video";
export { VideoTrack } from "twilio-video";
